import _ from 'lodash'
import moment from 'moment'

import { constants } from '../constants'
import { strings } from '../context/LanguageContext/strings'
import { util } from '../util/index'

export const common = {
    joinClasses: (...args) =>
        args
            .map(list => list)
            .filter(Boolean)
            .join(' '),

    findCountry: stateCode => {
        const mergeRegions = constants.usRegions.concat(constants.canadaRegions)
        const region = mergeRegions.find(({ state }) => state === stateCode)
        return region ? region.country : undefined
    },

    parseCountryCode: country => {
        const countries = { US: 'United States', CA: 'Canada' }
        return countries[country]
    },

    getSorting: (order, orderBy) =>
        order === 'desc'
            ? (a, b) => common.desc(a, b, orderBy)
            : (a, b) => -common.desc(a, b, orderBy),

    desc: (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) {
            return -1
        }
        if (b[orderBy] > a[orderBy]) {
            return 1
        }
        return 0
    },

    sessionExpiresInMS: expiresAt => expiresAt * 1000 - new Date().getTime(),

    stableSort: (array, cmp) => {
        const stabilizedThis = array.map((el, index) => {
            return [el, index]
        })
        stabilizedThis.sort((a, b) => {
            const order = cmp(a[0], b[0])
            return order !== 0 ? order : a[1] - b[1]
        })
        return stabilizedThis.map(el => el[0])
    },

    thousands_separators: num => {
        const num_parts = num.toString().split('.')
        num_parts[0] = num_parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        return num_parts.join('.')
    },

    intToString: value => {
        const suffixes = ['', 'k', 'm', 'b', 't']
        const suffixNum = Math.floor(('' + value).length / 4)
        let shortValue = parseFloat(
            (suffixNum !== 0 ? value / Math.pow(1000, suffixNum) : value).toPrecision(2)
        )
        if (shortValue % 1 !== 0) {
            shortValue = shortValue.toFixed(1)
        }
        return shortValue + suffixes[suffixNum]
    },

    deepShallowHelper: (state, prop, field, payload) => {
        /* Shallow Copy */
        const updatedState = { ...state }
        /* Deep Copy */
        const selectOptions = { ...updatedState[field] }
        /*  Change value */
        selectOptions[prop] = payload
        /* updating Repeat/End object to updatedState */
        updatedState[field] = selectOptions
        return updatedState
    },

    deepShallowArrayHelper: (state, prop, field, payload) => {
        const updatedState = { ...state }
        const selectOptions = { ...updatedState[field] }
        selectOptions[prop] = [...selectOptions[prop], payload]
        updatedState[field] = selectOptions
        return updatedState
    },

    deepShallowFilterHelper: (state, prop, field, payload) => {
        const updatedState = { ...state }
        const selectOptions = { ...updatedState[field] }
        selectOptions[prop] = selectOptions[prop].filter(el => el !== payload)
        updatedState[field] = selectOptions
        return updatedState
    },

    cloneArrOfObj: arr => arr.map(obj => ({ ...obj })),

    isArraysEqual: (a, b) =>
        Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every(el => b.includes(el)),

    shallowHelper: (state, updatedFields) => ({ ...state, ...updatedFields }),

    /**
     *
     * @param list: {Array} ['a','b','c','d']
     * @returns {String} a, b, c, d
     */
    getStringsFromArray: list => {
        if (!list) {
            return null
        }
        return list.reduce(
            (acc, item, i) => (i === 0 ? (item === 'all' ? 'National' : item) : `${acc}, ${item}`),
            ''
        )
    },

    getMaxIndex: (list, maxChar) => {
        let maxLength = 0
        let maxIndex = 0

        for (let i = 0; i < list.length; i++) {
            if (maxLength <= maxChar) {
                maxLength += list[i].length
            } else {
                maxIndex = i
                break
            }
        }

        /**
         * The following operation is to make sure, if the characters
         * are less than maxChar then return the length of the list but not 0
         */
        maxIndex = maxIndex === 0 ? list.length : maxIndex

        return maxIndex
    },

    promoUpdatedBy: (data, email) => {
        data.promo['updated_by'] = email
        return data
    },

    regexString: string => string.replace(/\s\s+/g, ' ').trim(),

    findUniqueValues: (list, value) => {
        const uniqueSet = new Set()
        list.forEach(el => {
            if (el[value]) {
                uniqueSet.add(el[value])
            }
        })
        return [...uniqueSet.values()]
    },

    findUniqueNestedValues: (list, prop, nestedProp) => {
        const uniqueSet = new Set()
        list.forEach(el => {
            if (el[prop]) {
                uniqueSet.add(el[prop][nestedProp])
            }
        })
        return [...uniqueSet.values()]
    },

    getScheduleDateRange: (startDate, endDate, language) => {
        moment.locale(language)
        return `${common.getFormattedDate(startDate, 'MMM D')} - ${common.getFormattedDate(
            endDate,
            'MMM D, yyyy'
        )}`
    },

    getMax: array => Math.max.apply(Math, array),

    getMin: array => Math.min.apply(Math, array),

    searchByInput: (value, list) => {
        let searchedList = []
        if (list && list.length > 0) {
            searchedList = list.filter(item =>
                item.toString().toLowerCase().includes(value.toString().toLowerCase())
            )
        }

        return searchedList
    },

    /**
     *
     * @param value {date}
     * @param formatValue {format}
     * @returns {string} Example format'24 Mar, 2020'
     */
    getFormattedDate: (value, formatValue) => (value ? moment(value).format(formatValue) : null),

    getKeyByValue: (object, value) => {
        return value
            ? Object.keys(object).find(key => object[key].toLowerCase() === value.toLowerCase())
            : null
    },

    filterExistedKeyValues: data => {
        Object.keys(data).forEach(key => {
            if (!data[key]) {
                delete data[key]
            }
        })
        return data
    },

    clearNullAndUndefined: obj => {
        Object.keys(obj).forEach(key => {
            if (obj[key] === null || obj[key] === undefined) {
                delete obj[key]
            }
        })
    },

    getBiggerByAttr: (list, attr) => {
        if (!list) return
        return list.reduce((a, b) => (a[attr] > b[attr] ? a : b))
    },

    getPartialContentDateRange: (startDate, endDate) =>
        `${common.getFormattedDate(startDate, 'MMM D, yyyy hh:mm a')} - ${common.getFormattedDate(
            endDate,
            'MMM D, yyyy hh:mm a'
        )}`,

    replaceStrUNS: str => str.replace(' ', '_'),
    sortDays: function (a, b) {
        const weeks = constants.getWeekDays()
        const days = weeks.map(day => day.value.toUpperCase())
        return days.indexOf(a) - days.indexOf(b)
    },
    getDateRangeDisplayPill: (startDate, endDate) => {
        let display_value = ''
        if (startDate && endDate) {
            display_value = `${common.getFormattedDate(
                startDate,
                'MMM D'
            )} - ${common.getFormattedDate(endDate, 'MMM D, yyyy')}`
        } else if (startDate) {
            display_value = `${strings.FROM} ${common.getFormattedDate(startDate, 'MMM D, yyyy')}`
        } else if (endDate) {
            display_value = `${strings.UNTIL} ${common.getFormattedDate(endDate, 'MMM D, yyyy')}`
        }
        return display_value
    },
    getSuggestList: ({ value, list, baseFilterList }) => {
        const cloneList = [...list]

        if (value && value.length && cloneList.indexOf(value) === -1) {
            cloneList.push(value)
        }

        const updatedList = cloneList.reduce((acc, cur) => {
            const matchItems = util.searchByInput(cur, baseFilterList)
            return acc.concat(matchItems || cur)
        }, [])

        return updatedList.length ? _.uniq(updatedList) : value.length ? [value] : []
    },
    getLowerCase: word => {
        return word ? word.toLowerCase().trim() : null
    },
    getFormattedPhoneNumber: phoneNum => {
        if (!phoneNum) return null
        let filteredNum = phoneNum.replace(/\D/g, '')

        //Check if the input is of correct length
        let validNum = filteredNum.match(/^(\d{3})(\d{3})(\d{4})$/)

        if (validNum) {
            return '(' + validNum[1] + ') ' + validNum[2] + ' - ' + validNum[3]
        }
    },
    filterFormProps: ({ isRequired, validate, groupBy, isFullWidth, isUpdating, ...rest }) => ({
        ...rest,
    }),
    getIdsFromList: ({ list, baseFilterList }) => {
        if (!baseFilterList || !baseFilterList.length) {
            return []
        }
        const object = baseFilterList.reduce((acc, { name, id }) => {
            acc[name.toLowerCase()] = id
            return acc
        }, {})

        return list.reduce((acc, item) => {
            const id = object[item.toLowerCase()]
            if (id) {
                acc.push(id)
            }
            return acc
        }, [])
    },
    validateIntegers: value => {
        return new RegExp(/^\d*\.?\d*$/).test(value)
    },
    searchFromList: (value, list) => {
        let searchedList = []
        if (list && list.length > 0) {
            searchedList = list.filter(item =>
                item.label.toString().toLowerCase().includes(value.toString().toLowerCase())
            )
        }

        return searchedList
    },
    setObjArrayFromList: list => {
        if (!list || list.length === 0) {
            return []
        } else {
            return list.map(item => ({
                label: item,
                value: item,
            }))
        }
    },
    setObjArrayFromLocalizedList: list => {
        return list.map(({ display }) => ({
            label: display,
            value: display,
        }))
    },
}
